import { useState, useMemo } from "react";

const CourseList = ({
  darkMode,
  courses,
  semesterType,
  getSemesterTypeDisplayName,
  fetchCourseSections,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const getSemesterDisplayName = (semester) => {
    return `Semester ${semester}`;
  };

  // Group courses by program code
  const groupedCourses = useMemo(() => {
    const groups = {};
    courses.forEach((course) => {
      const programCode = course.program_code || "Unknown Program";
      if (!groups[programCode]) {
        groups[programCode] = [];
      }
      groups[programCode].push(course);
    });
    return groups;
  }, [courses]);

  // Filter courses based on search query
  const filteredCourses = useMemo(() => {
    if (!searchQuery.trim()) return courses;

    const query = searchQuery.toLowerCase().trim();
    return courses.filter(
      (course) =>
        course.course_code.toLowerCase().includes(query) ||
        course.course_title.toLowerCase().includes(query)
    );
  }, [courses, searchQuery]);

  // Filter grouped courses based on search
  const filteredGroupedCourses = useMemo(() => {
    if (!searchQuery.trim()) return groupedCourses;

    const filteredGroups = {};
    Object.keys(groupedCourses).forEach((programCode) => {
      const filtered = groupedCourses[programCode].filter(
        (course) =>
          course.course_code
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          course.course_title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filtered.length > 0) {
        filteredGroups[programCode] = filtered;
      }
    });
    return filteredGroups;
  }, [groupedCourses, searchQuery]);

  return (
    <div
      className="rounded-lg p-4 mb-4 border"
      style={{
        backgroundColor: darkMode ? "#0D1117" : "#F8FAFC",
        borderColor: darkMode ? "#22232B" : "#D1D5DB",
        boxShadow: darkMode
          ? "none"
          : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
      }}
    >
      {/* Enhanced Header Section with Better Contrast */}
      <div
        className="rounded-lg p-4 mb-4"
        style={{
          backgroundColor: darkMode ? "#161B22" : "#FFFFFF",
          border: darkMode ? "1px solid #22232B" : "1px solid #E5E7EB",
          boxShadow: darkMode ? "none" : "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div
              className="w-2 h-6 rounded-full mr-3"
              style={{ backgroundColor: darkMode ? "#3B82F6" : "#2563EB" }}
            ></div>
            <h3
              className="text-lg font-semibold"
              style={{ color: darkMode ? "#FFFFFF" : "#111827" }}
            >
              Available Courses - {getSemesterTypeDisplayName(semesterType)}
            </h3>
          </div>
          <span
            className="text-sm font-medium px-3 py-1 rounded-full"
            style={{
              color: darkMode ? "#93C5FD" : "#1D4ED8",
              backgroundColor: darkMode ? "rgba(59, 130, 246, 0.2)" : "#EFF6FF",
              border: darkMode
                ? "1px solid rgba(59, 130, 246, 0.3)"
                : "1px solid #BFDBFE",
            }}
          >
            {filteredCourses.length}{" "}
            {filteredCourses.length === 1 ? "course" : "courses"} found
          </span>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by course code or name..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
              borderColor: darkMode ? "#374151" : "#D1D5DB",
              color: darkMode ? "#E5E7EB" : "#111827",
            }}
          />
        </div>
      </div>

      {/* Group courses by program code */}
      {Object.keys(filteredGroupedCourses).map((programCode) => (
        <div key={programCode} className="mb-8">
          {/* Enhanced Program Header */}
          <div
            className="rounded-lg p-4 mb-4 border"
            style={{
              backgroundColor: darkMode ? "#161B22" : "#F1F5F9",
              borderColor: darkMode ? "#22232B" : "#E2E8F0",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-8 rounded-full mr-3"
                  style={{ backgroundColor: darkMode ? "#3B82F6" : "#2563EB" }}
                ></div>
                <div>
                  <h4
                    className="text-lg font-bold"
                    style={{ color: darkMode ? "#FFFFFF" : "#111827" }}
                  >
                    {programCode}
                  </h4>
                  <p
                    className="text-sm"
                    style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
                  >
                    {filteredGroupedCourses[programCode].length} course
                    {filteredGroupedCourses[programCode].length !== 1
                      ? "s"
                      : ""}{" "}
                    in this program
                  </p>
                </div>
              </div>
              <div
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: darkMode
                    ? "rgba(59, 130, 246, 0.2)"
                    : "#EFF6FF",
                  color: darkMode ? "#93C5FD" : "#1D4ED8",
                  border: darkMode
                    ? "1px solid rgba(59, 130, 246, 0.3)"
                    : "1px solid #BFDBFE",
                }}
              >
                Program
              </div>
            </div>
          </div>

          {/* Courses Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGroupedCourses[programCode].map((course) => (
              <div
                key={course.section_id || course.course_id}
                onClick={() => fetchCourseSections(course)}
                className="p-4 rounded-lg cursor-pointer transition-all duration-200 border"
                style={{
                  backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
                  borderColor: darkMode ? "#22232B" : "#E5E7EB",
                  color: darkMode ? "#C9CCD1" : "#111827",
                  boxShadow: darkMode
                    ? "none"
                    : "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = darkMode
                    ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                    : "0 4px 12px rgba(0, 0, 0, 0.08)";
                  e.target.style.backgroundColor = darkMode
                    ? "#161B22"
                    : "#F9FAFB";
                  e.target.style.borderColor = darkMode ? "#3B82F6" : "#3B82F6";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = darkMode
                    ? "none"
                    : "0 1px 3px rgba(0, 0, 0, 0.05)";
                  e.target.style.backgroundColor = darkMode
                    ? "#0D1117"
                    : "#FFFFFF";
                  e.target.style.borderColor = darkMode ? "#22232B" : "#E5E7EB";
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4
                    className="font-bold text-base"
                    style={{ color: darkMode ? "#FFFFFF" : "#111827" }}
                  >
                    {course.course_code}
                  </h4>
                  <span
                    className="text-xs font-semibold px-2 py-1 rounded"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(59, 130, 246, 0.2)"
                        : "#EFF6FF",
                      color: darkMode ? "#93C5FD" : "#1D4ED8",
                      border: darkMode ? "none" : "1px solid #BFDBFE",
                    }}
                  >
                    {course.credits} Credits
                  </span>
                </div>

                <p
                  className="text-sm mb-3 leading-tight"
                  style={{ color: darkMode ? "#B0B3B8" : "#374151" }}
                >
                  {course.course_title}
                </p>

                {/* Additional Information Section */}
                <div className="space-y-2 mb-3">
                  {/* Section Name (if available) */}
                  {course.section_name && (
                    <div className="flex items-center text-xs">
                      <span
                        className="font-medium mr-1"
                        style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
                      >
                        Section:
                      </span>
                      <span style={{ color: darkMode ? "#FFFFFF" : "#111827" }}>
                        {course.section_name}
                      </span>
                    </div>
                  )}

                  {/* Attainment Status (if available) */}
                  {course.isAttainmentCalculated !== undefined && (
                    <div className="flex items-center text-xs">
                      <span
                        className="font-medium mr-1"
                        style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
                      >
                        Attainment:
                      </span>
                      <span
                        className="text-xs font-medium px-2 py-1 rounded"
                        style={{
                          backgroundColor: course.isAttainmentCalculated
                            ? darkMode
                              ? "rgba(16, 185, 129, 0.2)"
                              : "#ECFDF5"
                            : darkMode
                              ? "rgba(239, 68, 68, 0.2)"
                              : "#FEF2F2",
                          color: course.isAttainmentCalculated
                            ? darkMode
                              ? "#10B981"
                              : "#047857"
                            : darkMode
                              ? "#EF4444"
                              : "#DC2626",
                          border: course.isAttainmentCalculated
                            ? darkMode
                              ? "none"
                              : "1px solid #A7F3D0"
                            : darkMode
                              ? "none"
                              : "1px solid #FECACA",
                        }}
                      >
                        {course.isAttainmentCalculated
                          ? "Calculated"
                          : "Not Calculated"}
                      </span>
                    </div>
                  )}
                </div>

                <div
                  className="flex justify-between items-center mt-3 pt-3"
                  style={{
                    borderTop: darkMode
                      ? "1px solid #22232B"
                      : "1px solid #F3F4F6",
                  }}
                >
                  <span
                    className="text-xs font-medium px-2 py-1 rounded"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(16, 185, 129, 0.2)"
                        : "#ECFDF5",
                      color: darkMode ? "#10B981" : "#047857",
                      border: darkMode ? "none" : "1px solid #A7F3D0",
                    }}
                  >
                    {getSemesterDisplayName(course.semester)}
                  </span>

                  <span
                    className="text-xs font-medium px-2 py-1 rounded"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(249, 115, 22, 0.2)"
                        : "#FFEDD5",
                      color: darkMode ? "#FDBA74" : "#9A3412",
                      border: darkMode ? "none" : "1px solid #FED7AA",
                    }}
                  >
                    {course.semester_type === "odd" ? "Odd" : "Even"} Sem
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Show message when no courses match search */}
      {filteredCourses.length === 0 && searchQuery && (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p
            className="text-lg font-medium mb-2"
            style={{ color: darkMode ? "#E5E7EB" : "#111827" }}
          >
            No courses found
          </p>
          <p
            className="text-sm"
            style={{ color: darkMode ? "#9CA3AF" : "#6B7280" }}
          >
            No courses match "{searchQuery}". Try different keywords.
          </p>
        </div>
      )}
    </div>
  );
};

export default CourseList;
